import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/utils";

interface ScoreRingProps extends Omit<HTMLAttributes<HTMLDivElement>, "color"> {
  value?: number;
  max?: number;
  /** Diameter in px. */
  size?: number;
  /** Arc thickness in px. */
  stroke?: number;
  /** Arc colour — usually a risk token, e.g. "var(--risk-high)". */
  color?: string;
  /** Mono caption under the numeral, e.g. an already-translated "RYZYKO". */
  label?: ReactNode;
}

/**
 * ScoreRing — donut gauge for a risk / compliance score. Serif numeral in the
 * centre, optional mono caption below. Track is --surface-3; the arc colour is a
 * risk/brand token so it re-themes. Exposed as an ARIA meter for assistive tech.
 */
export function ScoreRing(
  {
    value = 0,
    max = 100,
    size = 72,
    stroke = 7,
    color = "var(--brand)",
    label,
    className,
    ...props
  }: ScoreRingProps,
) {
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const pct = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;

  return (
    <div
      data-slot="score-ring"
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      className={cn(
        "relative inline-flex items-center justify-center",
        className,
      )}
      style={{ width: size, height: size }}
      {...props}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--surface-3)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct)}
          className="transition-[stroke-dashoffset] duration-700 ease-[var(--ease-out)]"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className="font-heading font-bold leading-none text-[var(--ink)]"
          style={{ fontSize: size * 0.28 }}
        >
          {value}
        </span>
        {label && (
          <span className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.05em] text-[var(--ink-3)]">
            {label}
          </span>
        )}
      </div>
    </div>
  );
}
